import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { JwtHelperService } from '@auth0/angular-jwt';
import type { TokenInterface } from '../models/token.interface';
export const TOKEN_KEY = 'auth-token';
const USER_KEY = 'auth-user';
const USER_ID_KEY = 'auth-user-id';
const DEBUG_KEY = 'debug';
const REQUIRE_USER_DETAILS_KEY = 'require-user-details';
const CLINIC_ID_KEY = 'clinic-id';
const IS_MY_PACS_KEY = 'is-mypacs';
const ORIGIN_KEY = 'origin';
const REQUIRE_CGU_KEY = 'require-cgu';
@Injectable({
  providedIn: 'root'
})
export class TokenStorageService {
  token: BehaviorSubject<string> = new BehaviorSubject<string>('');
  user: BehaviorSubject<string> = new BehaviorSubject<string>('');
  userId: BehaviorSubject<number | undefined> = new BehaviorSubject<number | undefined>(undefined);
  requireUserDetails: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  clinicId: BehaviorSubject<number | undefined> = new BehaviorSubject<number | undefined>(undefined);
  isMyPacs: BehaviorSubject<boolean | undefined> = new BehaviorSubject<boolean | undefined>(undefined);
  origin: BehaviorSubject<string | undefined> = new BehaviorSubject<string | undefined>(undefined);
  requireCgu: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  constructor(private readonly cookieService: CookieService) {
    this.token.next(this.getToken() ?? '');
    this.user.next(this.getUser() ?? '');
    this.requireUserDetails.next(this.getRequireUserDetails());
    this.clinicId.next(this.getClinicId());
    this.isMyPacs.next(this.getIsMyPacs());
    this.origin.next(this.getOrigin());
    this.requireCgu.next(this.getRequireCgu());
  }

  signOut(): void {
    this.cookieService.deleteAll('/');
    this.token.next('');
    this.requireUserDetails.next(false);
    this.clinicId.next(undefined);
    this.isMyPacs.next(undefined);
    this.origin.next(undefined);
    this.requireCgu.next(false);
  }

  saveTokenAndUserInfo(tokenResult: string): void {
    const jwtHelperService = new JwtHelperService();
    // eslint-disable-next-line @typescript-eslint/ban-types
    const token: TokenInterface | null = jwtHelperService.decodeToken(tokenResult);
    if (token) {
      this.saveToken(tokenResult);
      this.saveUserId(parseInt(token.sub, 10));
      this.saveUser(token.name);

      const requireDetails = token.require_user_details === 'true';
      this.requireUserDetails.next(requireDetails);
      this.saveRequireUserDetails(requireDetails);

      // Extract branding claims from JWT token
      if (token.clinic_id) {
        const clinicId = parseInt(token.clinic_id, 10);
        this.saveClinicId(clinicId);
      }

      if (token.is_mypacs !== undefined) {
        const isMyPacs = token.is_mypacs === 'true';
        this.saveIsMyPacs(isMyPacs);
      }

      if (token.origin) {
        this.saveOrigin(token.origin);
      }

      const requireCgu = token.require_cgu === 'true';
      this.requireCgu.next(requireCgu);
      this.saveRequireCgu(requireCgu);
    }
  }

  saveToken(token: string): void {
    this.cookieService.delete(TOKEN_KEY, '/');
    this.cookieService.set(TOKEN_KEY, token, { expires: 90, path: '/' });
    this.token.next(token);
  }

  saveUser(user: string): void {
    this.cookieService.delete(USER_KEY, '/');
    this.cookieService.set(USER_KEY, user, { expires: 90, path: '/' });
    this.user.next(user);
  }

  saveUserId(userId: number): void {
    this.cookieService.delete(USER_ID_KEY, '/');
    this.cookieService.set(USER_ID_KEY, userId.toString(), { expires: 90, path: '/' });
    this.userId.next(userId);
  }

  saveRequireUserDetails(requireDetails: boolean): void {
    this.cookieService.delete(REQUIRE_USER_DETAILS_KEY, '/');
    this.cookieService.set(REQUIRE_USER_DETAILS_KEY, requireDetails.toString(), { expires: 90, path: '/' });
    this.requireUserDetails.next(requireDetails);
  }

  saveClinicId(clinicId: number): void {
    this.cookieService.delete(CLINIC_ID_KEY, '/');
    this.cookieService.set(CLINIC_ID_KEY, clinicId.toString(), { expires: 90, path: '/' });
    this.clinicId.next(clinicId);
  }

  saveIsMyPacs(isMyPacs: boolean): void {
    this.cookieService.delete(IS_MY_PACS_KEY, '/');
    this.cookieService.set(IS_MY_PACS_KEY, isMyPacs.toString(), { expires: 90, path: '/' });
    this.isMyPacs.next(isMyPacs);
  }

  saveOrigin(origin: string): void {
    this.cookieService.delete(ORIGIN_KEY, '/');
    this.cookieService.set(ORIGIN_KEY, origin, { expires: 90, path: '/' });
    this.origin.next(origin);
  }

  getRequireUserDetails(): boolean {
    const requireDetails = this.cookieService.get(REQUIRE_USER_DETAILS_KEY);
    return requireDetails === 'true';
  }

  getClinicId(): number | undefined {
    const clinicIdStr = this.cookieService.get(CLINIC_ID_KEY);
    return clinicIdStr ? parseInt(clinicIdStr, 10) : undefined;
  }

  getIsMyPacs(): boolean | undefined {
    const isMyPacsStr = this.cookieService.get(IS_MY_PACS_KEY);
    if (isMyPacsStr === 'true') return true;
    if (isMyPacsStr === 'false') return false;
    return undefined;
  }

  getOrigin(): string | undefined {
    const origin = this.cookieService.get(ORIGIN_KEY);
    return origin || undefined;
  }

  markUserDetailsCompleted(): void {
    this.saveRequireUserDetails(false);
  }

  saveRequireCgu(requireCgu: boolean): void {
    this.cookieService.delete(REQUIRE_CGU_KEY, '/');
    this.cookieService.set(REQUIRE_CGU_KEY, requireCgu.toString(), { expires: 90, path: '/' });
    this.requireCgu.next(requireCgu);
  }

  getRequireCgu(): boolean {
    const requireCgu = this.cookieService.get(REQUIRE_CGU_KEY);
    return requireCgu === 'true';
  }

  markCguAccepted(): void {
    this.saveRequireCgu(false);
  }

  setDebug(state: boolean): void {
    this.cookieService.set(DEBUG_KEY, state ? '1' : '0', { expires: 90, path: '/' });
  }

  getUserId(): number | undefined {
    return parseInt(this.cookieService.get(USER_ID_KEY), 10);
  }

  getToken(): string | undefined {
    return this.cookieService.get(TOKEN_KEY);
  }

  getUser(): string | undefined {
    return this.cookieService.get(USER_KEY);
  }

  isDebug(): boolean {
    return this.cookieService.get(DEBUG_KEY) === '1';
  }
}
